import type { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { color, diagramStyle } from './palette';

/**
 * The trust cycle: sources disagree → an exception is raised → a person reviews → resolved → the period's exceptions
 * close → trust state, and round again next period. Reconciliation and notice variance enter the same loop.
 */
export const TrustCycle: FC = () => {
  const theme = useTheme();
  const sub = { fill: color.muted, fontSize: 13 } as const;
  const row = { fontSize: 14, fill: color.fg } as const;
  const node = (cx: number, cy: number, w: number, text: string, stroke: string, fill?: string) => (
    <g key={text}>
      <rect x={cx - w / 2} y={cy - 21} width={w} height="42" rx="21" fill={fill ?? 'none'} fillOpacity={fill ? 0.15 : 1} stroke={stroke} strokeWidth="2.5" />
      <text x={cx} y={cy + 5} textAnchor="middle" style={row}>
        {text}
      </text>
    </g>
  );
  const arrow = (x1: number, y1: number, x2: number, y2: number) => <line key={`${x1}-${y1}`} x1={x1} y1={y1} x2={x2} y2={y2} stroke={color.muted} strokeWidth="2" markerEnd="url(#academy-arrow-trust)" />;

  return (
    <svg viewBox="0 0 940 430" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="The trust cycle" style={diagramStyle(theme)}>
      <defs>
        <marker id="academy-arrow-trust" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto">
          <path d="M0,0 L8,4 L0,8 z" fill={color.muted} />
        </marker>
      </defs>
      {/* Two kinds of exception, one loop */}
      {node(400, 35, 160, 'reconciliation', color.muted, color.muted)}
      {node(640, 35, 160, 'notice variance', color.muted, color.muted)}
      {arrow(400, 58, 480, 102)}
      {arrow(640, 58, 560, 102)}
      {/* The loop */}
      {node(520, 125, 180, 'exception raised', color.warn)}
      {node(260, 200, 170, 'sources disagree', color.muted)}
      {node(780, 200, 170, 'a person reviews', color.accent)}
      {node(780, 320, 170, 'resolved', color.muted)}
      {node(520, 395, 250, "period's exceptions closed", color.muted)}
      {node(260, 320, 170, 'trust state', color.ok, color.ok)}
      {arrow(347, 185, 433, 140)}
      {arrow(607, 140, 693, 185)}
      {arrow(780, 223, 780, 297)}
      {arrow(693, 335, 620, 372)}
      {arrow(420, 372, 347, 335)}
      {arrow(260, 297, 260, 223)}
      <text x="248" y="265" textAnchor="end" style={sub}>
        next period
      </text>
      <text x="520" y="265" textAnchor="middle" style={sub}>
        human in the loop
      </text>
    </svg>
  );
};
